import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Select,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Chip,
  Menu,
  Tabs,
  Tab,
  Divider,
  FormControl,
  InputLabel,
  CircularProgress,
  Grid,
  Stack,
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Email as EmailIcon,
  PersonAdd as PersonAddIcon,
  Settings as SettingsIcon,
  CreditCard as CreditCardIcon,
  People as PeopleIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Cancel as CancelIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useAuthStore } from '../../store/authStore';
import { userService } from '../../services/api/user';
import { billingApi } from '../../services/api/billing';
import { Subscription, SubscriptionTier, BillingFrequency, SubscriptionStatus, PRICING_TIERS } from '../../types/billing';
import { ChangePlanDialog } from './ChangePlanDialog';

interface OrganizationUser {
  id: string;
  email: string;
  fullName: string;
  role: 'owner' | 'admin' | 'member';
  joinedAt: string;
  emailVerified: boolean;
  isActive: boolean;
  isPending?: boolean;
  invitedBy?: string;
}

export const OrganizationSettings: React.FC = () => {
  const { user } = useAuthStore();
  const [users, setUsers] = useState<OrganizationUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = useState<OrganizationUser | null>(null);
  const [tabValue, setTabValue] = useState(0);
  
  // Subscription management state
  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [subscriptionLoading, setSubscriptionLoading] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [changePlanDialogOpen, setChangePlanDialogOpen] = useState(false);
  const [selectedTier, setSelectedTier] = useState<SubscriptionTier>(SubscriptionTier.PROFESSIONAL);
  const [selectedFrequency, setSelectedFrequency] = useState<BillingFrequency>(BillingFrequency.MONTHLY);
  const [selectedToolkit, setSelectedToolkit] = useState<string>('');
  const [newUserCount, setNewUserCount] = useState(1);
  const [availableSlots, setAvailableSlots] = useState(0);
  const [addUserCount, setAddUserCount] = useState(1); // State for the add users input field

  // Debug logging for organization role recognition issue
  console.log('[OrganizationSettings] Debug - User object received:', user);
  console.log('[OrganizationSettings] Debug - User object stringified:', JSON.stringify(user, null, 2));
  console.log('[OrganizationSettings] Debug - organizationRole value:', user?.organizationRole);
  console.log('[OrganizationSettings] Debug - organizationRole type:', typeof user?.organizationRole);
  console.log('[OrganizationSettings] Debug - organizationRole === "owner":', user?.organizationRole === 'owner');
  console.log('[OrganizationSettings] Debug - organizationRole === "admin":', user?.organizationRole === 'admin');
  console.log('[OrganizationSettings] Debug - All user keys:', user ? Object.keys(user) : 'no user');
  
  // Check localStorage directly to see what's stored
  const storedUser = localStorage.getItem('user');
  console.log('[OrganizationSettings] Debug - localStorage user:', storedUser);
  if (storedUser) {
    try {
      const parsedStoredUser = JSON.parse(storedUser);
      console.log('[OrganizationSettings] Debug - Parsed localStorage user:', parsedStoredUser);
      console.log('[OrganizationSettings] Debug - Parsed organizationRole:', parsedStoredUser?.organizationRole);
    } catch (e) {
      console.log('[OrganizationSettings] Debug - Failed to parse localStorage user:', e);
    }
  }

  // Check if current user is admin or owner
  // Use localStorage as fallback if store user is not available
  const parsedStoredUser = storedUser ? JSON.parse(storedUser) : null;
  const currentUserRole = user?.organizationRole || parsedStoredUser?.organizationRole;
  const isAdmin = currentUserRole === 'admin' || currentUserRole === 'owner';
  const isOwner = currentUserRole === 'owner';
  
  console.log('[OrganizationSettings] Debug - currentUserRole:', currentUserRole);
  console.log('[OrganizationSettings] Debug - isAdmin:', isAdmin);
  console.log('[OrganizationSettings] Debug - isOwner:', isOwner);

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      console.log('[OrganizationSettings] Loading organization data...');

      // Load organization users
      const usersResponse = await userService.getOrganizationUsers();
      console.log('[OrganizationSettings] Users response:', usersResponse);
      console.log('[OrganizationSettings] Total users:', usersResponse.data?.length);
      console.log('[OrganizationSettings] Active users:', usersResponse.data?.filter(u => !u.isPending).length);
      console.log('[OrganizationSettings] Pending invitations:', usersResponse.data?.filter(u => u.isPending).length);
      console.log('[OrganizationSettings] User details:', usersResponse.data?.map(u => ({
        email: u.email,
        fullName: u.fullName,
        isPending: u.isPending,
        role: u.role
      })));
      setUsers(usersResponse.data || []);
    } catch (err: any) {
      console.error('[OrganizationSettings] Failed to load organization data:', err);
      console.error('[OrganizationSettings] Error details:', {
        message: err.message,
        response: err.response?.data,
        status: err.response?.status
      });
      setError('Failed to load organization settings');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadSubscriptionData = useCallback(async () => {
    try {
      setSubscriptionLoading(true);

      if (user?.organizationId) {
        console.log('[OrganizationSettings] Loading subscription data for org:', user.organizationId);
        const subscriptionData = await billingApi.getSubscription(user.organizationId);
        console.log('[OrganizationSettings] Subscription data:', subscriptionData);
        setSubscription(subscriptionData);

        // Re-fetch users to ensure we have the latest count
        const usersResponse = await userService.getOrganizationUsers();
        const currentUsers = usersResponse.data || [];
        setUsers(currentUsers);

        // Calculate available slots for new user invitations
        const currentUserCount = currentUsers.length;
        const subscriptionUserCount = subscriptionData.userCount || 1;
        const available = subscriptionUserCount - currentUserCount;
        setAvailableSlots(Math.max(0, available));
      }
    } catch (err: any) {
      console.error('[OrganizationSettings] Failed to load subscription data:', err);
      // Don't set general error for subscription failures
      console.warn('Subscription data not available - user may not have billing set up yet');
    } finally {
      setSubscriptionLoading(false);
    }
  }, [user?.organizationId]);

  useEffect(() => {
    console.log('[OrganizationSettings] useEffect triggered, isAdmin:', isAdmin, 'currentUserRole:', currentUserRole, 'dataLoaded:', dataLoaded);
    
    // Load data if user is admin/owner and data hasn't been loaded yet
    if (isAdmin && !dataLoaded) {
      console.log('[OrganizationSettings] User is admin and data not loaded yet, loading data...');
      setDataLoaded(true); // Set this first to prevent multiple loads
      loadData();
      loadSubscriptionData();
    } else if (currentUserRole && currentUserRole !== 'admin' && currentUserRole !== 'owner') {
      console.log('[OrganizationSettings] User is not admin/owner, not loading data');
      setLoading(false);
    } else {
      console.log('[OrganizationSettings] Skipping load - isAdmin:', isAdmin, 'dataLoaded:', dataLoaded);
    }
  }, [currentUserRole, dataLoaded, isAdmin, loadData, loadSubscriptionData]); // Run when currentUserRole changes
  
  // Listen for organization user updates from other components
  useEffect(() => {
    const handleUserUpdate = (event: CustomEvent) => {
      console.log('[OrganizationSettings] Organization users updated event received:', event.detail);
      // Only reload if this component initiated the change to avoid double loading
      if (event.detail?.source !== 'organization-settings') {
        loadData();
        loadSubscriptionData();
      }
    };
    
    window.addEventListener('organization-users-updated', handleUserUpdate as EventListener);
    
    return () => {
      window.removeEventListener('organization-users-updated', handleUserUpdate as EventListener);
    };
  }, [loadData, loadSubscriptionData]);


  const handleRoleChange = async (userId: string, newRole: string) => {
    try {
      await userService.updateUserRole(userId, newRole);
      await loadData();
    } catch (err) {
      console.error('Failed to update user role:', err);
      setError('Failed to update user role');
    }
  };

  const handleRemoveUser = async (userId: string) => {
    if (window.confirm('Are you sure you want to remove this user from the organization?')) {
      try {
        console.log('[OrganizationSettings] Removing user:', userId);
        await userService.removeUserFromOrganization(userId);
        console.log('[OrganizationSettings] User removed successfully, reloading data...');
        
        // The removeUserFromOrganization method will dispatch an event
        // We'll reload data here immediately for this component
        await loadData();
        console.log('[OrganizationSettings] Data reloaded after user removal');
        
        await loadSubscriptionData(); // Refresh subscription data to update available slots
        console.log('[OrganizationSettings] Subscription data reloaded');
        
        // Also dispatch the event with a source identifier so this component knows not to reload again
        window.dispatchEvent(new CustomEvent('organization-users-updated', {
          detail: { action: 'user-removed', userId, source: 'organization-settings' }
        }));
      } catch (err) {
        console.error('[OrganizationSettings] Failed to remove user:', err);
        setError('Failed to remove user from organization');
      }
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleUpdateUserCount = async (newCount: number) => {
    if (!subscription || !user?.organizationId) return;
    
    // Validate the new count
    const currentCount = subscription.userCount || 0;
    if (newCount === currentCount) {
      setError('User count is already set to this value');
      return;
    }
    
    // Minimum of 1 seat required
    if (newCount < 1) {
      setError('Subscription must have at least 1 seat');
      return;
    }
    
    // Check if reducing seats below current active users
    if (newCount < users.length) {
      setError(`Cannot reduce seats below the number of active users (${users.length})`);
      return;
    }
    
    try {
      setSubscriptionLoading(true);
      await billingApi.updateSubscription(subscription.id, {
        seatCount: newCount
      });
      await loadSubscriptionData();
      // Reset the add user count to match the new subscription count
      setAddUserCount(newCount);
    } catch (err: any) {
      console.error('Failed to update user count:', err);
      setError('Failed to update subscription user count');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleChangePlan = async () => {
    if (!subscription || !user?.organizationId) return;
    
    try {
      setSubscriptionLoading(true);
      
      // Debug logging
      console.log('Updating subscription with:', {
        tier: selectedTier,
        billingFrequency: selectedFrequency,
        seatCount: newUserCount,
        selectedToolkit: selectedTier === SubscriptionTier.INDUSTRY_SPECIFIC ? selectedToolkit : undefined,
        selectedToolkitValue: selectedToolkit
      });
      
      await billingApi.updateSubscription(subscription.id, {
        tier: selectedTier,
        billingFrequency: selectedFrequency,
        seatCount: newUserCount,
        selectedToolkit: selectedTier === SubscriptionTier.INDUSTRY_SPECIFIC ? selectedToolkit : undefined
      });
      await loadSubscriptionData();
      setChangePlanDialogOpen(false);
    } catch (err: any) {
      console.error('Failed to change plan:', err);
      setError('Failed to change subscription plan');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const handleCancelSubscription = async () => {
    if (!subscription || !cancelReason.trim()) return;
    
    try {
      setSubscriptionLoading(true);
      // Note: The billing API might need to be updated to accept a cancellation reason
      await billingApi.cancelSubscription(subscription.id);
      await loadSubscriptionData();
      setCancelDialogOpen(false);
      setCancelReason('');
    } catch (err: any) {
      console.error('Failed to cancel subscription:', err);
      setError('Failed to cancel subscription');
    } finally {
      setSubscriptionLoading(false);
    }
  };

  const getTierDisplayName = (tier: SubscriptionTier): string => {
    const tierInfo = PRICING_TIERS.find(t => t.tier === tier);
    return tierInfo?.name || tier;
  };

  const getTierPrice = (tier?: SubscriptionTier, frequency?: BillingFrequency): number => {
    if (!tier || !frequency) return 0;
    
    const tierInfo = PRICING_TIERS.find(t => t.tier === tier);
    if (!tierInfo) return 0;
    
    switch (frequency) {
      case BillingFrequency.MONTHLY:
        return tierInfo.price.monthly;
      case BillingFrequency.QUARTERLY:
        return tierInfo.price.quarterly;
      case BillingFrequency.YEARLY:
        return tierInfo.price.yearly;
      default:
        return tierInfo.price.monthly;
    }
  };


  const getRoleColor = (role: string) => {
    switch (role) {
      case 'owner':
        return 'error';
      case 'admin':
        return 'warning';
      default:
        return 'default';
    }
  };

  if (!isAdmin) {
    return (
      <div style={{ padding: 24 }}>
        <Alert severity="info">
          Only organization administrators and owners can access these settings.
        </Alert>
      </div>
    );
  }

  return (
    <div>
      {error && (
        <Alert severity="error" style={{ marginBottom: 16 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Tabs value={tabValue} onChange={handleTabChange} style={{ borderBottom: '1px solid rgba(0,0,0,0.12)', marginBottom: 24 }}>
        <Tab icon={<PeopleIcon />} label="Organization Users" />
        <Tab icon={<CreditCardIcon />} label="Subscription Management" />
      </Tabs>

      {/* Organization Users Tab */}
      {tabValue === 0 && (
        <Card>
        <CardContent>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
            <Typography variant="h6" gutterBottom>
              Organization Users
            </Typography>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button
                variant="outlined"
                onClick={async () => {
                  console.log('[OrganizationSettings] Manual reload button clicked');
                  console.log('[OrganizationSettings] Current state - loading:', loading, 'dataLoaded:', dataLoaded, 'users:', users);
                  setDataLoaded(false);
                  try {
                    await loadData();
                    console.log('[OrganizationSettings] Manual reload completed');
                  } catch (error) {
                    console.error('[OrganizationSettings] Manual reload failed:', error);
                  }
                }}
                disabled={loading}
              >
                Reload
              </Button>
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={() => setInviteDialogOpen(true)}
              >
                Invite User
              </Button>
            </div>
          </div>

          {/* Active Members Table */}
          <Typography variant="subtitle1" style={{ marginBottom: 16, fontWeight: 500 }}>
            Active Members
          </Typography>
          <TableContainer component={Paper} variant="outlined" style={{ marginBottom: 24 }}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>User</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Joined</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.filter(u => !u.isPending).map((orgUser) => (
                  <TableRow key={orgUser.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {orgUser.fullName}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <Typography variant="body2">{orgUser.email}</Typography>
                        {!orgUser.emailVerified && (
                          <Chip label="Unverified" size="small" color="warning" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {isOwner && orgUser.id !== user?.id ? (
                        <Select
                          value={orgUser.role}
                          onChange={(e) => handleRoleChange(orgUser.id, e.target.value)}
                          size="small"
                        >
                          <MenuItem value="member">Member</MenuItem>
                          <MenuItem value="admin">Admin</MenuItem>
                          <MenuItem value="owner">Owner</MenuItem>
                        </Select>
                      ) : (
                        <Chip
                          label={orgUser.role}
                          color={getRoleColor(orgUser.role)}
                          size="small"
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={orgUser.isActive ? 'Active' : 'Inactive'}
                        color={orgUser.isActive ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(orgUser.joinedAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      {isOwner && orgUser.id !== user?.id && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            setAnchorEl(e.currentTarget);
                            setSelectedUser(orgUser);
                          }}
                        >
                          <MoreVertIcon />
                        </IconButton>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
                {users.filter(u => !u.isPending).length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      <Typography variant="body2" color="text.secondary">
                        No active members
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Pending Invitations Table */}
          {users.filter(u => u.isPending).length > 0 && (
            <>
              <Typography variant="subtitle1" style={{ marginBottom: 16, fontWeight: 500 }}>
                Pending Invitations
              </Typography>
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Email</TableCell>
                      <TableCell>Role</TableCell>
                      <TableCell>Invited</TableCell>
                      <TableCell>Invited By</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.filter(u => u.isPending).map((invitation) => (
                      <TableRow key={invitation.id}>
                        <TableCell>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Typography variant="body2">{invitation.email}</Typography>
                            <Chip label="Pending" size="small" color="info" icon={<EmailIcon />} />
                          </div>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={invitation.role}
                            color={getRoleColor(invitation.role)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(invitation.joinedAt).toLocaleDateString()}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {invitation.invitedBy || 'System'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          {isOwner && (
                            <IconButton
                              size="small"
                              onClick={(e) => {
                                setAnchorEl(e.currentTarget);
                                setSelectedUser(invitation);
                              }}
                            >
                              <MoreVertIcon />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          )}
        </CardContent>
      </Card>
      )}

      {/* Subscription Management Tab */}
      {tabValue === 1 && (
        <div>
          {subscriptionLoading && (
            <div style={{ display: 'flex', justifyContent: 'center', padding: 16 }}>
              <CircularProgress />
            </div>
          )}
          
          {subscription ? (
            <Grid container spacing={3}>
              {/* Current Subscription Info */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Current Subscription
                    </Typography>
                    <Stack spacing={2}>
                      <div>
                        <Typography variant="body2" color="text.secondary">
                          Plan
                        </Typography>
                        <Typography variant="h6">
                          {getTierDisplayName(subscription.planTier)}
                        </Typography>
                      </div>
                      <div>
                        <Typography variant="body2" color="text.secondary">
                          Billing Frequency
                        </Typography>
                        <Typography variant="body1">
                          {subscription.billingFrequency ?
                            subscription.billingFrequency.charAt(0).toUpperCase() + subscription.billingFrequency.slice(1) :
                            'Not specified'
                          }
                        </Typography>
                      </div>
                      <div>
                        <Typography variant="body2" color="text.secondary">
                          User Seats
                        </Typography>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Typography variant="h6">
                            {users.length} / {subscription.userCount}
                          </Typography>
                          <Chip
                            size="small"
                            label={availableSlots > 0 ? `${availableSlots} available` : 'At capacity'}
                            color={availableSlots > 0 ? 'success' : 'warning'}
                          />
                        </div>
                      </div>
                      <div>
                        <Typography variant="body2" color="text.secondary">
                          Status
                        </Typography>
                        <Chip
                          icon={
                            subscription.status === SubscriptionStatus.ACTIVE ? <CheckCircleIcon /> :
                            subscription.status === SubscriptionStatus.TRIALING ? <CheckCircleIcon /> :
                            subscription.status === SubscriptionStatus.PAST_DUE ? <WarningIcon /> :
                            <CancelIcon />
                          }
                          label={
                            subscription.status === SubscriptionStatus.ACTIVE ? 'Active' :
                            subscription.status === SubscriptionStatus.TRIALING ? 'Trialing' :
                            subscription.status === SubscriptionStatus.PAST_DUE ? 'Past Due' :
                            subscription.status === SubscriptionStatus.CANCELED ? 'Canceled' :
                            subscription.status === SubscriptionStatus.SUSPENDED ? 'Suspended' :
                            'Unknown'
                          }
                          color={
                            subscription.status === SubscriptionStatus.ACTIVE ? 'success' :
                            subscription.status === SubscriptionStatus.TRIALING ? 'success' :
                            subscription.status === SubscriptionStatus.PAST_DUE ? 'warning' :
                            subscription.status === SubscriptionStatus.CANCELED ? 'error' :
                            subscription.status === SubscriptionStatus.SUSPENDED ? 'error' :
                            'default'
                          }
                        />
                      </div>
                      <div>
                        <Typography variant="body2" color="text.secondary">
                          Next Billing Date
                        </Typography>
                        <Typography variant="body1">
                          {subscription.currentPeriodEnd ?
                            new Date(subscription.currentPeriodEnd).toLocaleDateString() :
                            'Not specified'
                          }
                        </Typography>
                      </div>
                      <div>
                        <Typography variant="body2" color="text.secondary">
                          Monthly Cost
                        </Typography>
                        <Typography variant="h6">
                          ${subscription.totalMonthlyAmount ? subscription.totalMonthlyAmount.toFixed(2) : '0.00'}
                        </Typography>
                      </div>
                      {subscription.planTier === SubscriptionTier.INDUSTRY_SPECIFIC && subscription.selectedToolkit && (
                        <div>
                          <Typography variant="body2" color="text.secondary">
                            Selected Toolkit
                          </Typography>
                          <Chip
                            label={subscription.selectedToolkit.replace('_TOOLKIT', '').replace('_', ' ')}
                            color="primary"
                            size="small"
                          />
                        </div>
                      )}
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>

              {/* Subscription Actions */}
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Manage Subscription
                    </Typography>
                    <Stack spacing={2}>
                      {/* Update User Count */}
                      <div>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Update Total Seats
                        </Typography>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <TextField
                            type="number"
                            size="small"
                            value={addUserCount}
                            onChange={(e) => {
                              const newCount = parseInt(e.target.value) || 1;
                              // Allow any value >= 1 (minimum 1 seat required)
                              if (newCount >= 1) {
                                setAddUserCount(newCount);
                              }
                            }}
                            inputProps={{
                              min: 1,
                              step: 1
                            }}
                            style={{ width: 100 }}
                          />
                          <Button
                            variant="contained"
                            color={addUserCount < (subscription.userCount || 0) ? 'warning' : 'primary'}
                            startIcon={addUserCount > (subscription.userCount || 0) ? <TrendingUpIcon /> : 
                                      addUserCount < (subscription.userCount || 0) ? <TrendingDownIcon /> : null}
                            onClick={() => handleUpdateUserCount(addUserCount)}
                            disabled={subscriptionLoading || addUserCount === (subscription.userCount || 0)}
                          >
                            Update to {addUserCount} Total Seat{addUserCount !== 1 ? 's' : ''}
                          </Button>
                        </div>
                        <Typography variant="caption" color="text.secondary">
                          {addUserCount !== (subscription.userCount || 0) && (() => {
                            const pricePerSeat = getTierPrice(subscription.planTier, subscription.billingFrequency);
                            const currentCount = subscription.userCount || 0;
                            const newTotal = addUserCount * pricePerSeat;
                            const difference = (addUserCount - currentCount) * pricePerSeat;
                            
                            return (
                              <>
                                New monthly cost: {addUserCount} seats × ${pricePerSeat.toFixed(2)} = 
                                ${newTotal.toFixed(2)}/month
                                {' '}
                                {addUserCount > currentCount && (
                                  <span>(+${Math.abs(difference).toFixed(2)} from current)</span>
                                )}
                                {addUserCount < currentCount && (
                                  <span>(-${Math.abs(difference).toFixed(2)} from current)</span>
                                )}
                              </>
                            );
                          })()}
                        </Typography>
                      </div>

                      <Divider />

                      {/* Change Plan */}
                      <div>
                        <Button
                          variant="contained"
                          startIcon={<SettingsIcon />}
                          onClick={() => {
                            setSelectedTier(subscription.planTier);
                            setSelectedFrequency(subscription.billingFrequency || BillingFrequency.MONTHLY);
                            // Initialize with existing toolkit or default to MEP_TOOLKIT if industry_specific
                            const initialToolkit = subscription.selectedToolkit || 
                              (subscription.planTier === SubscriptionTier.INDUSTRY_SPECIFIC ? 'MEP_TOOLKIT' : '');
                            setSelectedToolkit(initialToolkit);
                            console.log('Initializing dialog with toolkit:', initialToolkit);
                            setNewUserCount(subscription.userCount || 1);
                            setChangePlanDialogOpen(true);
                          }}
                          disabled={subscriptionLoading}
                          fullWidth
                        >
                          Change Plan
                        </Button>
                      </div>

                      <Divider />

                      {/* Cancel Subscription */}
                      <div>
                        <Button
                          variant="outlined"
                          color="error"
                          startIcon={<CancelIcon />}
                          onClick={() => setCancelDialogOpen(true)}
                          disabled={subscriptionLoading || subscription.cancelAtPeriodEnd}
                          fullWidth
                        >
                          {subscription.cancelAtPeriodEnd ? 'Cancellation Scheduled' : 'Cancel Subscription'}
                        </Button>
                        {subscription.cancelAtPeriodEnd && (
                          <Typography variant="caption" color="error" display="block" style={{ marginTop: 8 }}>
                            Subscription will end on {subscription.currentPeriodEnd ? new Date(subscription.currentPeriodEnd).toLocaleDateString() : 'end of current period'}
                          </Typography>
                        )}
                      </div>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : (
            <Card>
              <CardContent>
                <Alert severity="info">
                  <Typography variant="body1">
                    No active subscription found. Contact your billing administrator to set up a subscription.
                  </Typography>
                </Alert>
              </CardContent>
            </Card>
          )}
        </div>
      )}

      {/* User Actions Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
      >
        <MenuItem
          onClick={() => {
            if (selectedUser) {
              handleRemoveUser(selectedUser.id);
            }
            setAnchorEl(null);
          }}
        >
          Remove from Organization
        </MenuItem>
      </Menu>

      {/* Invite User Dialog */}
      <InviteUserDialog
        open={inviteDialogOpen}
        onClose={() => setInviteDialogOpen(false)}
        onInvite={() => {
          loadData();
          loadSubscriptionData();
        }}
      />
      
      {/* Change Plan Dialog */}
      <ChangePlanDialog
        open={changePlanDialogOpen}
        onClose={() => setChangePlanDialogOpen(false)}
        onConfirm={handleChangePlan}
        subscription={subscription}
        selectedTier={selectedTier}
        setSelectedTier={setSelectedTier}
        selectedFrequency={selectedFrequency}
        setSelectedFrequency={setSelectedFrequency}
        selectedToolkit={selectedToolkit}
        setSelectedToolkit={setSelectedToolkit}
        newUserCount={newUserCount}
        setNewUserCount={setNewUserCount}
        loading={subscriptionLoading}
      />
      
      
      {/* Cancel Subscription Dialog */}
      <CancelSubscriptionDialog
        open={cancelDialogOpen}
        onClose={() => setCancelDialogOpen(false)}
        onConfirm={handleCancelSubscription}
        cancelReason={cancelReason}
        setCancelReason={setCancelReason}
        loading={subscriptionLoading}
      />
    </div>
  );
};

// Invite User Dialog Component
interface InviteUserDialogProps {
  open: boolean;
  onClose: () => void;
  onInvite: () => void;
}

const InviteUserDialog: React.FC<InviteUserDialogProps> = ({ open, onClose, onInvite }) => {
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [role, setRole] = useState<'member' | 'admin'>('member');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useAuthStore();
  const [availableSlots, setAvailableSlots] = useState(0);
  
  useEffect(() => {
    const checkAvailableSlots = async () => {
      if (open && user?.organizationId) {
        try {
          const subscription = await billingApi.getSubscription(user.organizationId);
          const users = await userService.getOrganizationUsers();
          const available = subscription.userCount - (users.data?.length || 0);
          setAvailableSlots(available);
        } catch (err) {
          console.error('Failed to check available slots:', err);
          setAvailableSlots(0);
        }
      }
    };
    
    checkAvailableSlots();
  }, [open, user?.organizationId]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError(null);

      if (!email) {
        setError('Please enter an email address');
        return;
      }

      if (!firstName || !lastName) {
        setError('Please enter first and last name');
        return;
      }
      
      if (availableSlots <= 0) {
        setError('No available subscription slots. Please upgrade your subscription or remove users first.');
        return;
      }

      await userService.inviteUser({ 
        email, 
        firstName,
        lastName,
        role 
      });
      onInvite();
      onClose();
      setEmail('');
      setFirstName('');
      setLastName('');
      setRole('member');
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to send invitation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Invite User to Organization</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" style={{ marginBottom: 16 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <div style={{ paddingTop: 16 }}>
          {availableSlots > 0 ? (
            <Alert severity="success" style={{ marginBottom: 16 }}>
              <Typography variant="body2">
                You have {availableSlots} available subscription slot{availableSlots !== 1 ? 's' : ''}
              </Typography>
            </Alert>
          ) : (
            <Alert severity="warning" style={{ marginBottom: 16 }}>
              <Typography variant="body2">
                No available subscription slots. Please upgrade your subscription or remove users first.
              </Typography>
            </Alert>
          )}
          
          <TextField
            fullWidth
            label="Email Address"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            margin="normal"
            helperText="An invitation email will be sent to this address"
            disabled={availableSlots <= 0}
          />

          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                margin="normal"
                disabled={availableSlots <= 0}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                margin="normal"
                disabled={availableSlots <= 0}
              />
            </Grid>
          </Grid>

          <TextField
            fullWidth
            select
            label="Role"
            value={role}
            onChange={(e) => setRole(e.target.value as 'member' | 'admin')}
            margin="normal"
            helperText="Select the role for the new user"
          >
            <MenuItem value="member">Member</MenuItem>
            <MenuItem value="admin">Admin</MenuItem>
          </TextField>
        </div>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading || availableSlots <= 0}
          startIcon={<EmailIcon />}
        >
          {loading ? 'Sending...' : 'Send Invitation'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};


// Cancel Subscription Dialog Component
interface CancelSubscriptionDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  cancelReason: string;
  setCancelReason: (reason: string) => void;
  loading: boolean;
}

const CancelSubscriptionDialog: React.FC<CancelSubscriptionDialogProps> = ({
  open,
  onClose,
  onConfirm,
  cancelReason,
  setCancelReason,
  loading
}) => {
  const cancellationReasons = [
    'Too expensive',
    'Not using enough features',
    'Found a better alternative',
    'Downsizing team',
    'Temporary pause',
    'Technical issues',
    'Other'
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Cancel Subscription</DialogTitle>
      <DialogContent>
        <Alert severity="warning" style={{ marginBottom: 24 }}>
          <Typography variant="body2">
            Your subscription will remain active until the end of your current billing period.
            You can reactivate anytime before then.
          </Typography>
        </Alert>
        
        <FormControl fullWidth>
          <InputLabel>Reason for cancellation</InputLabel>
          <Select
            value={cancelReason}
            label="Reason for cancellation"
            onChange={(e) => setCancelReason(e.target.value)}
          >
            {cancellationReasons.map((reason) => (
              <MenuItem key={reason} value={reason}>
                {reason}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        
        {cancelReason === 'Other' && (
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Please specify"
            margin="normal"
            onChange={(e) => setCancelReason(`Other: ${e.target.value}`)}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Keep Subscription</Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          color="error"
          disabled={loading || !cancelReason}
        >
          {loading ? 'Canceling...' : 'Cancel Subscription'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};